import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { SiteHeader } from "../components/layout/SiteHeader";
import { SiteFooter } from "../components/layout/SiteFooter";
import { openCalendly } from "../utils/calendly";
import { logContact, logEvent } from "../utils/analytics";

const TOPICS = [
  "API Integration",
  "Starter / Growth Plan",
  "Lifetime Plan",
  "Enterprise / Custom Volume",
  "Partnership",
  "Other",
];

const inputCls =
  "w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-white placeholder:text-slate-500 outline-none transition focus:border-primary/70 focus:bg-white/10 text-sm";

export function ContactSalesPage() {
  const [form, setForm]       = useState({ name: '', company: '', phone: '', email: '', topic: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent]       = useState(false);
  const [error, setError]     = useState('');

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { setError('Name and email are required.'); return; }
    setError('');
    setSubmitting(true);
    logContact({ ...form });
    logEvent({ name: 'contact_form_submitted', type: 'form', meta: { topic: form.topic } });
    // Fire-and-forget to Sheets — give it a moment then show success
    await new Promise((r) => setTimeout(r, 600));
    setSubmitting(false);
    setSent(true);
  };

  return (
    <div className="relative flex min-h-screen w-full flex-col overflow-x-hidden bg-background-dark text-white">
      <Helmet>
        <title>Contact Sales — Container Optimisation Software | Feuteuristix</title>
        <meta name="description" content="Get in touch with the Feuteuristix team. Questions about API integration, enterprise pricing, or partnerships — we respond within one business day." />
        <meta property="og:title" content="Contact Sales — Container Optimisation Software | Feuteuristix" />
        <meta property="og:description" content="Get in touch with the Feuteuristix team. Questions about API integration, enterprise pricing, or partnerships — we respond within one business day." />
        <meta property="og:url" content="https://feuteuristix.com/contact-sales" />
        <meta name="twitter:title" content="Contact Sales — Container Optimisation Software | Feuteuristix" />
        <meta name="twitter:description" content="Get in touch with the Feuteuristix team. Questions about API integration, enterprise pricing, or partnerships — we respond within one business day." />
        <link rel="canonical" href="https://feuteuristix.com/contact-sales" />
      </Helmet>
      <SiteHeader />

      <main className="flex-1 pt-28 pb-16">
        <div className="mx-auto max-w-2xl px-6">

          {/* Header */}
          <div className="mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Get in touch</p>
            <h1 className="mt-3 text-4xl font-black leading-tight md:text-5xl">
              Talk to us
            </h1>
            <p className="mt-4 text-slate-300 leading-relaxed">
              Fill the form and we'll get back to you within one business day.
              Rather call or email directly? Those work too.
            </p>

            {/* Direct contact strip */}
            <div className="mt-6 flex flex-wrap gap-4">
              <a
                href="mailto:contact@feuteuristix.com"
                onClick={() => logEvent({ name: 'contact_email_click', type: 'click' })}
                className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-5 py-2.5 text-sm font-semibold text-slate-200 transition hover:border-primary/50 hover:bg-white/10 hover:text-white"
              >
                <span className="material-symbols-outlined text-[18px] text-primary">mail</span>
                contact@feuteuristix.com
              </a>
              <a
                href="tel:8840311800"
                onClick={() => logEvent({ name: 'contact_phone_click', type: 'click' })}
                className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-5 py-2.5 text-sm font-semibold text-slate-200 transition hover:border-primary/50 hover:bg-white/10 hover:text-white"
              >
                <span className="material-symbols-outlined text-[18px] text-primary">call</span>
                +91 88403 11800
              </a>
            </div>
          </div>

          {/* Form card */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-8 md:p-10">
            {sent ? (
              <div className="flex flex-col items-center gap-4 py-8 text-center">
                <div className="flex size-16 items-center justify-center rounded-full bg-emerald-500/15">
                  <span className="material-symbols-outlined text-4xl text-emerald-400" style={{ fontVariationSettings: "'FILL' 1" }}>check_circle</span>
                </div>
                <h2 className="text-2xl font-black text-white">Message received</h2>
                <p className="text-slate-300 max-w-sm">
                  We'll get back to you at <span className="font-semibold text-white">{form.email}</span> within one business day.
                </p>
                <Link
                  to="/"
                  className="mt-4 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-primary to-blue-500 px-6 py-3 text-sm font-bold text-white transition hover:brightness-110"
                >
                  Back to home
                  <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <label className="flex flex-col gap-2">
                    <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Name <span className="text-primary">*</span></span>
                    <input className={inputCls} type="text" placeholder="Your name" value={form.name} onChange={set('name')} required />
                  </label>
                  <label className="flex flex-col gap-2">
                    <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Company</span>
                    <input className={inputCls} type="text" placeholder="Company or platform" value={form.company} onChange={set('company')} />
                  </label>
                </div>

                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <label className="flex flex-col gap-2">
                    <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Work email <span className="text-primary">*</span></span>
                    <input className={inputCls} type="email" placeholder="you@company.com" value={form.email} onChange={set('email')} required />
                  </label>
                  <label className="flex flex-col gap-2">
                    <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Phone</span>
                    <input className={inputCls} type="tel" placeholder="+91 98765 43210" value={form.phone} onChange={set('phone')} />
                  </label>
                </div>

                <label className="flex flex-col gap-2">
                  <span className="text-xs font-bold uppercase tracking-widest text-slate-400">How can we help?</span>
                  <select
                    className={inputCls + " appearance-none cursor-pointer"}
                    value={form.topic}
                    onChange={set('topic')}
                  >
                    <option value="" className="bg-slate-900">Select a topic…</option>
                    {TOPICS.map((t) => (
                      <option key={t} value={t} className="bg-slate-900">{t}</option>
                    ))}
                  </select>
                </label>

                <label className="flex flex-col gap-2">
                  <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Message</span>
                  <textarea
                    className={inputCls + " resize-none h-32"}
                    placeholder="Tell us about your use case, volume, or what you're trying to solve…"
                    value={form.message}
                    onChange={set('message')}
                  />
                </label>

                {error && (
                  <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-400">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="group flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-primary via-blue-500 to-cyan-500 px-8 py-3.5 font-bold text-white transition-all hover:brightness-110 hover:scale-[1.02] active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <span className="material-symbols-outlined animate-spin text-[18px]">progress_activity</span>
                      Sending…
                    </>
                  ) : (
                    <>
                      Send message
                      <span className="material-symbols-outlined text-[18px] transition-transform group-hover:translate-x-1">arrow_forward</span>
                    </>
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Demo CTA */}
          <div className="mt-8 rounded-2xl border border-primary/20 bg-primary/5 p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <p className="font-bold text-white">Want to see it in action first?</p>
              <p className="mt-1 text-sm text-slate-400">Book a 20-minute live demo with the team; we'll pack your actual cargo in real time.</p>
            </div>
            <button
              onClick={() => {
                logEvent({ name: 'get_demo_click', type: 'cta', meta: { source: 'contact_page' } });
                openCalendly('https://calendly.com/sajal-agrawal-feuteuristix');
              }}
              className="shrink-0 flex items-center gap-2 rounded-xl border border-primary/50 px-5 py-2.5 text-sm font-bold text-primary transition hover:bg-primary/10 hover:border-primary whitespace-nowrap"
            >
              <span className="material-symbols-outlined text-[18px]">calendar_month</span>
              Book a demo
            </button>
          </div>

        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
